import type { FC, HTMLAttributes } from "react";
import type { Placement } from "react-aria";
import type { DialogProps as AriaDialogProps } from "react-aria-components";

import {
	ChevronSelectorVertical,
	Contrast01,
	LogOut01,
	User01,
} from "@untitledui/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFocusManager } from "react-aria";
import {
	Button as AriaButton,
	Dialog as AriaDialog,
	DialogTrigger as AriaDialogTrigger,
	Popover as AriaPopover,
} from "react-aria-components";
import { useNavigate } from "react-router";

import { AvatarLabelGroup } from "@/components/ui/avatar/avatar-label-group";
import { ROUTES } from "@/constants/routes";
import { useMe } from "@/features/users/hooks";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useLogout } from "@/hooks/useLogout";
import { useSettings } from "@/hooks/useSettings";
import { useAuth } from "@/providers/auth-provider";
import { cx } from "@/utils/cx";
import { formatUserName } from "@/utils/users";

export const NavAccountMenu = ({
	className,
	...dialogProps
}: AriaDialogProps & { className?: string }) => {
	const navigate = useNavigate();
	const focusManager = useFocusManager();
	const dialogRef = useRef<HTMLDivElement>(null);
	const { setIsSettingsModalOpen } = useSettings();
	const { authUser } = useAuth();
	const { userData } = useMe();
	const handleLogout = useLogout();

	const userInfo = useMemo(() => {
		if (userData) {
			return {
				avatar: authUser ? authUser.picture || "" : "",
				email: userData.email,
				name:
					userData.name ||
					formatUserName(userData.firstName, userData.lastName),
			};
		}

		if (!authUser) {
			return {
				avatar: "",
				email: "",
				name: "",
			};
		}

		return {
			avatar: authUser ? authUser.picture || "" : "",
			email: authUser.email || "",
			name:
				formatUserName(authUser.firstName, authUser.lastName) ||
				authUser.email ||
				"",
		};
	}, [userData, authUser]);
	const onKeyDown = useCallback(
		(e: KeyboardEvent) => {
			switch (e.key) {
				case "ArrowDown":
					focusManager?.focusNext({ tabbable: true, wrap: true });
					break;
				case "ArrowUp":
					focusManager?.focusPrevious({ tabbable: true, wrap: true });
					break;
			}
		},
		[focusManager],
	);

	useEffect(() => {
		const element = dialogRef.current;
		if (element) {
			element.addEventListener("keydown", onKeyDown);
		}

		return () => {
			if (element) {
				element.removeEventListener("keydown", onKeyDown);
			}
		};
	}, [onKeyDown]);

	return (
		<AriaDialog
			{...dialogProps}
			className={cx(
				"w-66 rounded-xl bg-secondary_alt shadow-lg ring ring-secondary_alt outline-hidden",
				className,
			)}
			ref={dialogRef}
		>
			<div className="flex flex-col gap-2 border-b border-secondary p-3">
				<AvatarLabelGroup
					size="md"
					src={userInfo.avatar}
					subtitle={userInfo.email}
					title={userInfo.name}
				/>
			</div>
			<div className="flex flex-col gap-0.5 py-1.5">
				<NavAccountCardMenuItem
					icon={User01}
					label="Manage Profile"
					onClick={() => {
						navigate(ROUTES.MANAGE_PROFILE);
					}}
				/>
				<NavAccountCardMenuItem
					icon={Contrast01}
					label="Choose Color Theme"
					onClick={() => setIsSettingsModalOpen(true)}
				/>
			</div>

			<div className="border-t border-secondary pt-1 pb-1.5">
				<NavAccountCardMenuItem
					icon={LogOut01}
					label="Sign out"
					onClick={handleLogout}
				/>
			</div>
		</AriaDialog>
	);
};

const NavAccountCardMenuItem = ({
	icon: Icon,
	label,
	shortcut,
	...buttonProps
}: HTMLAttributes<HTMLButtonElement> & {
	icon?: FC<{ className?: string }>;
	label: string;
	shortcut?: string;
}) => {
	return (
		<button
			{...buttonProps}
			className={cx(
				"group/item w-full cursor-pointer px-1.5 focus:outline-hidden",
				buttonProps.className,
			)}
		>
			<div
				className={cx(
					"flex w-full items-center justify-between gap-3 rounded-md p-2 group-hover/item:bg-primary_hover",
					// Focus styles.
					"outline-focus-ring group-focus-visible/item:outline-2 group-focus-visible/item:outline-offset-2",
				)}
			>
				<div className="flex gap-2 text-sm font-semibold text-secondary group-hover/item:text-secondary_hover">
					{Icon && <Icon className="size-5 text-fg-quaternary" />} {label}
				</div>

				{shortcut && (
					<kbd className="flex rounded px-1 py-px font-body text-xs font-medium text-tertiary ring-1 ring-secondary ring-inset">
						{shortcut}
					</kbd>
				)}
			</div>
		</button>
	);
};

export const NavAccountCard = ({
	popoverPlacement,
}: {
	popoverPlacement?: Placement;
}) => {
	const triggerRef = useRef<HTMLButtonElement>(null);
	const isDesktop = useBreakpoint("lg");
	const { isSettingsModalOpen } = useSettings();
	const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
	const { authUser } = useAuth();
	const { userData } = useMe();

	const userInfo = useMemo(() => {
		if (userData) {
			return {
				avatar: authUser ? authUser.picture || "" : "",
				email: userData.email,
				name:
					userData.name ||
					formatUserName(userData.firstName, userData.lastName),
				status: "online" as const,
			};
		}

		if (!authUser) {
			return {
				avatar: "",
				email: "",
				name: "",
				status: "offline" as const,
			};
		}

		return {
			avatar: authUser.picture || "",
			email: authUser.email || "",
			name:
				formatUserName(authUser.firstName, authUser.lastName) ||
				authUser.email ||
				"",
			status: "online" as const,
		};
	}, [userData, authUser]);

	// Close the account menu dialog when settings modal opens
	useEffect(() => {
		if (isSettingsModalOpen) {
			// Update state asynchronously to avoid cascading renders
			queueMicrotask(() => {
				setIsAccountMenuOpen(false);
			});
		}
	}, [isSettingsModalOpen]);

	return (
		<AriaDialogTrigger
			isOpen={isAccountMenuOpen}
			onOpenChange={setIsAccountMenuOpen}
		>
			<AriaButton
				className="group relative flex w-full cursor-pointer items-start gap-3 rounded-xl bg-primary p-3 text-left ring-1 ring-secondary outline-focus-ring transition-colors duration-100 ease-linear ring-inset hover:bg-primary_hover focus-visible:outline-2 focus-visible:outline-offset-2"
				ref={triggerRef}
			>
				<AvatarLabelGroup
					size="md"
					src={userInfo.avatar}
					subtitle={userInfo.email}
					title={userInfo.name}
				/>

				<div className="ml-auto flex shrink-0 items-center justify-center text-fg-quaternary group-hover:text-fg-quaternary_hover">
					<ChevronSelectorVertical className="size-4" />
				</div>

				<AriaPopover
					className={({ isEntering, isExiting }) =>
						cx(
							"origin-(--trigger-anchor-point) will-change-transform",
							isEntering &&
								"duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5",
							isExiting &&
								"duration-100 ease-in animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5",
						)
					}
					offset={8}
					placement={
						popoverPlacement ?? (isDesktop ? "right bottom" : "top right")
					}
					triggerRef={triggerRef}
				>
					<NavAccountMenu />
				</AriaPopover>
			</AriaButton>
		</AriaDialogTrigger>
	);
};
